import ProxyLayer from 'base/api/ProxyLayer';
import track from 'utils/amplitudeWrapper';
import { COUNTRY_NAME_TO_ALPHA2 } from 'utils/constants/Crop';

const SECTION_ID_MAP: Record<number, string> = {
  1: 'hero-banner-0a',
  2: 'hero-banner-0b',
  3: 'report-overview',
  4: 'soil-characteristics',
  5: 'expert-seed-advice',
  6: 'product-comparison',
  7: 'product-performance',
  8: 'agronomic-preferences',
  9: 'environmental-stress',
  10: 'trial-information',
  11: 'growth-stages',
  12: 'herbicide-technology',
  13: 'seed-density',
};

interface TimeRange {
  start: string;
  end: string;
}

interface LayoutItem {
  layout: string[];
  isSpecificHours: boolean;
  dateRange: [string, string];
  variant: string;
  timeRange: TimeRange[];
}

interface RoleLayouts {
  dateAndTime: LayoutItem[];
}

interface CountryItem {
  country: string;
  grower?: RoleLayouts;
  seller?: RoleLayouts;
}

interface LayoutResponse {
  items: {
    countries: CountryItem[];
  };
}

const DEFAULT_SECTION_ORDER = [
  'hero-banner-0a',
  'hero-banner-0b',
  'report-overview',
  'soil-characteristics',
  'expert-seed-advice',
  'product-comparison',
  'product-performance',
  'agronomic-preferences',
  'environmental-stress',
  'trial-information',
  'growth-stages',
  'herbicide-technology',
  'seed-density',
];

function calculateDatePriority(dateRange: [string, string], referenceDate: string): number {
  const [startDate, endDate] = dateRange;
  const refTime = new Date(referenceDate).getTime();
  const startTime = new Date(startDate).getTime();
  const endTime = new Date(endDate).getTime();

  if (refTime >= startTime && refTime <= endTime) {
    return 10000;
  }

  const distanceToStart = Math.abs(refTime - startTime);
  const distanceToEnd = Math.abs(refTime - endTime);
  const minDistance = Math.min(distanceToStart, distanceToEnd);

  const daysAway = minDistance / (1000 * 60 * 60 * 24);
  return Math.max(0, 1000 - daysAway);
}

function calculateTotalMinutes(timeRanges: TimeRange[]): number {
  return timeRanges.reduce((sum, range) => {
    const [startHour, startMin] = range.start.split(':').map(Number);
    const [endHour, endMin] = range.end.split(':').map(Number);
    const startInMin = startHour * 60 + startMin;
    const endInMin = endHour * 60 + endMin;
    return sum + (endInMin - startInMin);
  }, 0);
}

function sortLayoutsByPriority(layouts: LayoutItem[], referenceDate: string): LayoutItem[] {
  return [...layouts].sort((a, b) => {
    const aPriority = calculateDatePriority(a.dateRange, referenceDate);
    const bPriority = calculateDatePriority(b.dateRange, referenceDate);
    if (aPriority !== bPriority) {
      return bPriority - aPriority;
    }

    // Sort by time range duration (shortest duration gets higher priority)
    const aDuration = calculateTotalMinutes(a.timeRange);
    const bDuration = calculateTotalMinutes(b.timeRange);
    if (aDuration !== bDuration) {
      return aDuration - bDuration;
    }

    return 0;
  });
}

function isCurrentTimeInRanges(timeRanges: TimeRange[]): boolean {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeInMinutes = currentHour * 60 + currentMinute;

  for (const range of timeRanges) {
    const [startHour, startMin] = range.start.split(':').map(Number);
    const [endHour, endMin] = range.end.split(':').map(Number);

    const startTimeInMinutes = startHour * 60 + startMin;
    const endTimeInMinutes = endHour * 60 + endMin;

    const isInRange =
      currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes < endTimeInMinutes;

    if (isInRange) {
      return true;
    }
  }
  return false;
}

function isDateInRange(dateRange: [string, string], currentDate: string): boolean {
  const [startDate, endDate] = dateRange;

  const start = new Date(startDate);
  start.setDate(start.getDate() - 1);
  const startStr = start.toISOString().split('T')[0];

  const end = new Date(endDate);
  end.setDate(end.getDate() + 1);
  const endStr = end.toISOString().split('T')[0];

  const isInRange = currentDate > startStr && currentDate < endStr;

  return isInRange;
}

function convertLayoutToSectionIds(numericLayout: string[]): string[] {
  return numericLayout
    .map((id) => {
      const numId = Number.parseInt(id, 10);
      return SECTION_ID_MAP[numId];
    })
    .filter((id): id is string => id !== undefined);
}

function applyDefaultLayout(
  setIsSpecialUser: (value: boolean) => void,
  setSectionOrder: (value: string[]) => void,
  setIsVariantD: (value: boolean) => void
): void {
  setIsSpecialUser(false);
  setSectionOrder(DEFAULT_SECTION_ORDER);
  setIsVariantD(false);
}

function formatDateYYYYMMDD(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;
}

function findMatchingLayout(layouts: LayoutItem[], todayStr: string): LayoutItem | null {
  return (
    layouts.find((layout) => {
      const dateMatch = isDateInRange(layout.dateRange, todayStr);
      const timeMatch = isCurrentTimeInRanges(layout.timeRange);
      return dateMatch && timeMatch;
    }) ?? null
  );
}

export async function updateLayout(
  setIsSpecialUser: (value: boolean) => void,
  setSectionOrder: (value: string[]) => void,
  setIsVariantD: (value: boolean) => void,
  userCountry: string,
  userRole: string
): Promise<void> {
  const todayStr = formatDateYYYYMMDD(new Date());

  try {
    const layoutsResponse = await ProxyLayer.getLayouts();
    if (!layoutsResponse) {
      setIsSpecialUser(false);
      setIsVariantD(false);
      return;
    }
    const normalizedCountry = userCountry.trim();
    const userCountryAlpha2 =
      COUNTRY_NAME_TO_ALPHA2[normalizedCountry] || normalizedCountry.toLowerCase().substring(0, 2);

    const countryConfig = layoutsResponse.items.countries.find(
      (c: { country: string }) => c.country.toLowerCase() === userCountryAlpha2
    );

    if (!countryConfig) {
      applyDefaultLayout(setIsSpecialUser, setSectionOrder, setIsVariantD);
      return;
    }
    const normalizedRole = userRole.toLowerCase().trim();
    const roleLayouts: RoleLayouts | undefined =
      normalizedRole === 'grower' ? countryConfig.grower : countryConfig.seller;

    if (!roleLayouts?.dateAndTime?.length) {
      applyDefaultLayout(setIsSpecialUser, setSectionOrder, setIsVariantD);
      return;
    }
    const sortedLayouts = sortLayoutsByPriority(roleLayouts.dateAndTime, todayStr);
    const matchingLayout = findMatchingLayout(sortedLayouts, todayStr);
    if (matchingLayout) {
      const sectionIds = convertLayoutToSectionIds(matchingLayout.layout);

      setIsSpecialUser(true);
      setSectionOrder(sectionIds);
      const isVariantD = matchingLayout.variant.toLowerCase().includes('d');
      setIsVariantD(isVariantD);

      track('Custom Layout Applied', {
        country: userCountry,
        role: userRole,
        variant: matchingLayout.variant,
        dateRange: matchingLayout.dateRange,
        isSpecificHours: matchingLayout.isSpecificHours,
        layout: sectionIds,
      });
    } else {
      applyDefaultLayout(setIsSpecialUser, setSectionOrder, setIsVariantD);
      track('Default Layout Applied', {
        country: userCountry,
        role: userRole,
        reason: 'No matching date/time configuration',
        checkedLayouts: sortedLayouts.length,
      });
    }
  } catch (error) {
    applyDefaultLayout(setIsSpecialUser, setSectionOrder, setIsVariantD);
    track('Layout Error', {
      country: userCountry,
      role: userRole,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  }
}

export { SECTION_ID_MAP, DEFAULT_SECTION_ORDER };
export type { LayoutResponse, LayoutItem, TimeRange, RoleLayouts, CountryItem };
